<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  fetchMarketplaceJson,
  deleteMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;

const collections = ref([]);
const loading = ref(true);
const error = ref('');
const search = ref('');

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return collections.value;
  return collections.value.filter(c => c.title?.toLowerCase().includes(q));
});

async function loadData() {
  loading.value = true;
  error.value = '';
  try {
    const data = await fetchMarketplaceJson(accountId, 'collections');
    collections.value = data.collections || [];
  } catch (e) {
    error.value = 'Koleksiyonlar yüklenemedi: ' + e.message;
  } finally {
    loading.value = false;
  }
}

function newCollection() {
  router.push({ name: 'collection_new', params: { accountId } });
}
function editCollection(c) {
  router.push({ name: 'collection_edit', params: { accountId, id: c.id } });
}
async function removeCollection(c) {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`"${c.title}" koleksiyonu silinsin mi?`)) return;
  try {
    await deleteMarketplaceJson(accountId, `collections/${c.id}`);
    collections.value = collections.value.filter(x => x.id !== c.id);
  } catch (e) {
    error.value = 'Silinemedi: ' + e.message;
  }
}

onMounted(loadData);
</script>

<template>
  <div
    class="flex flex-col h-full flex-1 min-w-0 overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0"
    >
      <div>
        <h1 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Koleksiyonlar
        </h1>
        <p class="text-xs text-slate-400 mt-0.5">
          Ürün gruplama · vitrin · SEO{{
            collections.length ? ` (${collections.length})` : ''
          }}
        </p>
      </div>
      <button
        class="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600"
        @click="newCollection"
      >
        <span class="i-lucide-plus size-4" />
        Yeni Koleksiyon
      </button>
    </div>

    <div
      class="px-6 py-3 shrink-0 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800"
    >
      <div
        class="flex items-center gap-2 max-w-md px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus-within:ring-2 focus-within:ring-woot-500"
      >
        <span class="i-lucide-search size-4 shrink-0 text-slate-400" />
        <input
          v-model="search"
          type="text"
          placeholder="Koleksiyon ara…"
          class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-sm !bg-transparent !border-0 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-0"
        />
      </div>
    </div>

    <div class="flex-1 overflow-y-auto px-6 py-5">
      <div
        v-if="loading"
        class="flex items-center justify-center h-48 text-slate-400 gap-2"
      >
        <span class="i-lucide-loader-2 size-5 animate-spin" />
        <span class="text-sm">Yükleniyor…</span>
      </div>

      <div
        v-else-if="error"
        class="flex items-center gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm"
      >
        <span class="i-lucide-alert-circle size-5 shrink-0" />{{ error }}
      </div>

      <div
        v-else-if="filtered.length === 0"
        class="flex flex-col items-center justify-center h-64 text-slate-400 gap-3"
      >
        <span class="i-lucide-layout-grid size-12" />
        <span class="text-sm">Henüz koleksiyon yok.</span>
        <button
          class="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600"
          @click="newCollection"
        >
          <span class="i-lucide-plus size-4" /> Yeni Koleksiyon
        </button>
      </div>

      <div v-else class="space-y-2 max-w-4xl">
        <div
          v-for="c in filtered"
          :key="c.id"
          class="flex items-center gap-4 p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:shadow-sm transition cursor-pointer group"
          @click="editCollection(c)"
        >
          <div
            class="size-14 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0 flex items-center justify-center"
          >
            <img
              v-if="c.image"
              :src="c.image"
              class="w-full h-full object-cover"
            />
            <span v-else class="i-lucide-layout-grid size-6 text-slate-300" />
          </div>
          <div class="min-w-0 flex-1">
            <p
              class="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate"
            >
              {{ c.title }}
            </p>
            <p class="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
              <span class="inline-flex items-center gap-1">
                <span
                  :class="
                    c.collectionType === 'smart'
                      ? 'i-lucide-wand-2'
                      : 'i-lucide-hand'
                  "
                  class="size-3"
                />
                {{ c.collectionType === 'smart' ? 'Akıllı' : 'Manuel' }}
              </span>
              · {{ c.productCount }} ürün
            </p>
          </div>
          <span
            class="px-2 py-0.5 rounded-md text-xs font-medium shrink-0"
            :class="
              c.status === 'active'
                ? 'bg-green-500/90 text-white'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
            "
          >
            {{ c.status === 'active' ? 'Aktif' : 'Taslak' }}
          </span>
          <button
            class="text-slate-300 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100"
            @click.stop="removeCollection(c)"
          >
            <span class="i-lucide-trash-2 size-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
